import { useState, useRef, useEffect, useCallback } from "react";

const MIN_RECORDING_SECONDS = 5;
const MAX_RECORDING_SECONDS = 30;
const COUNTDOWN_START = 3;

export const RECORDER_PHASE = {
  IDLE: "idle",
  COUNTDOWN: "countdown",
  RECORDING: "recording",
  DONE: "done",
  ERROR: "error",
};

const useVoiceRecorder = () => {
  const [phase, setPhase] = useState(RECORDER_PHASE.IDLE);
  const [countdownValue, setCountdownValue] = useState(COUNTDOWN_START);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const maxDurationTimerRef = useRef(null);
  const elapsedTimerRef = useRef(null);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/ogg;codecs=opus";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blobType = mimeType.split(";")[0];
        const blob = new Blob(chunksRef.current, { type: blobType });
        setAudioBlob(blob);
        setPhase(RECORDER_PHASE.DONE);
        clearInterval(elapsedTimerRef.current);
        stopStream();
      };

      recorder.start();
      setPhase(RECORDER_PHASE.RECORDING);
      setElapsedSeconds(0);

      let elapsed = 0;
      elapsedTimerRef.current = setInterval(() => {
        elapsed += 1;
        setElapsedSeconds(elapsed);
      }, 1000);

      maxDurationTimerRef.current = setTimeout(() => {
        clearInterval(elapsedTimerRef.current);
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_SECONDS * 1000);
    } catch {
      setPhase(RECORDER_PHASE.ERROR);
    }
  }, [stopStream]);

  const start = useCallback(() => {
    setPhase(RECORDER_PHASE.COUNTDOWN);
    setCountdownValue(COUNTDOWN_START);
    setAudioBlob(null);

    let count = COUNTDOWN_START;

    const tick = () => {
      count -= 1;
      if (count > 0) {
        setCountdownValue(count);
        countdownTimerRef.current = setTimeout(tick, 1000);
      } else {
        setCountdownValue(0);
        startRecording();
      }
    };

    countdownTimerRef.current = setTimeout(tick, 1000);
  }, [startRecording]);

  const stop = useCallback(() => {
    clearTimeout(maxDurationTimerRef.current);
    clearInterval(elapsedTimerRef.current);
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    clearTimeout(countdownTimerRef.current);
    clearTimeout(maxDurationTimerRef.current);
    clearInterval(elapsedTimerRef.current);

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    stopStream();

    setPhase(RECORDER_PHASE.IDLE);
    setCountdownValue(COUNTDOWN_START);
    setElapsedSeconds(0);
    setAudioBlob(null);
  }, [stopStream]);

  useEffect(() => {
    return () => {
      clearTimeout(countdownTimerRef.current);
      clearTimeout(maxDurationTimerRef.current);
      clearInterval(elapsedTimerRef.current);
      stopStream();
    };
  }, [stopStream]);

  const canStop =
    phase === RECORDER_PHASE.RECORDING &&
    elapsedSeconds >= MIN_RECORDING_SECONDS;

  return {
    phase,
    countdownValue,
    elapsedSeconds,
    audioBlob,
    start,
    stop,
    reset,
    canStop,
    minSeconds: MIN_RECORDING_SECONDS,
    maxSeconds: MAX_RECORDING_SECONDS,
  };
};

export default useVoiceRecorder;
